import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatPrice, resolveImg } from "../lib/api";
import { useStore } from "../context/StoreContext";
import ProductCard from "../components/ProductCard";
import { ShareButton } from "../components/ShareButton";
import Seo from "../components/Seo";
import { Heart, ShieldCheck, Truck, Award, MessageCircle, ChevronRight } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const { addToCart, toggleWishlist, inWishlist } = useStore();

  useEffect(() => {
    setProduct(null); setActive(0);
    api.get(`/products/${slug}`).then(({ data }) => {
      setProduct(data);
      try {
        const prev = JSON.parse(localStorage.getItem("pa_recent") || "[]").filter((x) => x.id !== data.id);
        const entry = { id: data.id, title: data.title, price: data.price, images: data.images, slug: data.slug, size: data.size, technique: data.technique, in_stock: data.in_stock, category: data.category };
        localStorage.setItem("pa_recent", JSON.stringify([entry, ...prev].slice(0, 8)));
      } catch {}
    }).catch(() => navigate("/kartini"));
  }, [slug, navigate]);

  if (!product) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-charcoal/50">Зареждане...</div>;

  const images = product.images?.length ? product.images.map(resolveImg) : ["https://images.pexels.com/photos/1563016/pexels-photo-1563016.jpeg?auto=compress&cs=tinysrgb&w=900"];
  const liked = inWishlist(product.id);

  return (
    <div data-testid="product-detail">
      <Seo
        title={product.title}
        description={`${product.title} — оригинална ръчно рисувана картина${product.technique ? " с " + product.technique.toLowerCase() : ""}${product.size ? ", " + product.size : ""}. ${product.category_label || ""} от художник Силва Пенева.`}
        path={`/kartina/${product.slug || product.id}`}
        image={images[0]}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.title,
          image: images,
          description: (product.description || product.title).replace(/<[^>]+>/g, "").slice(0, 300),
          category: product.category_label,
          brand: { "@type": "Brand", name: "PenichArt" },
          offers: {
            "@type": "Offer",
            price: product.price,
            priceCurrency: "EUR",
            availability: product.in_stock ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
            url: `https://penichart.org/kartina/${product.slug || product.id}`,
          },
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-1.5 text-xs text-charcoal/50">
          <Link to="/" className="hover:text-gold">Начало</Link><ChevronRight size={12} />
          <Link to="/kartini" className="hover:text-gold">Картини</Link><ChevronRight size={12} />
          <span className="text-navy">{product.title}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 pb-16">
        {/* gallery */}
        <div>
          <div className="bg-white border border-stone overflow-hidden aspect-[4/5]">
            <img src={images[active]} alt={product.title} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className={`w-20 h-20 border ${i === active ? "border-gold" : "border-stone"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* info */}
        <div>
          <div className="flex gap-2 mb-4">
            {product.badges?.map((b) => (
              <span key={b} className="text-[10px] uppercase tracking-wider bg-navy text-white px-2.5 py-1">{b}</span>
            ))}
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-navy leading-tight">{product.title}</h1>
          <p className="font-serif text-3xl text-gold mt-4" data-testid="product-detail-price">{formatPrice(product.price)}</p>

          <div className="grid grid-cols-2 gap-3 mt-7 text-sm">
            {product.size && <div className="border border-stone p-3"><span className="text-charcoal/50 block text-xs">Размер</span><span className="text-navy font-medium">{product.size}</span></div>}
            <div className="border border-stone p-3"><span className="text-charcoal/50 block text-xs">Техника</span><span className="text-navy font-medium">{product.technique}</span></div>
            <div className="border border-stone p-3"><span className="text-charcoal/50 block text-xs">Категория</span><span className="text-navy font-medium">{product.category_label}</span></div>
            <div className="border border-stone p-3"><span className="text-charcoal/50 block text-xs">Наличност</span><span className="text-navy font-medium">{product.in_stock ? "Налично" : "Изчерпано"}</span></div>
          </div>

          <div className="flex gap-3 mt-7">
            <button onClick={() => addToCart(product)} className="btn-gold flex-1 py-3.5 rounded-sm" data-testid="add-to-cart-btn" disabled={!product.in_stock}>
              Добави в количката
            </button>
            <button onClick={() => toggleWishlist(product)} className={`w-14 flex items-center justify-center border ${liked ? "border-gold text-gold" : "border-navy text-navy"}`} data-testid="detail-wishlist-btn">
              <Heart size={20} fill={liked ? "#C6A45C" : "none"} />
            </button>
          </div>
          <button onClick={() => { addToCart(product); navigate("/checkout"); }} className="btn-navy-outline w-full py-3 rounded-sm mt-3" data-testid="buy-now-btn">
            Поръчай сега (наложен платеж)
          </button>

          <div className="mt-4">
            <ShareButton title={product.title} image={images[0]} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-7">
            {[[ShieldCheck, "Сигурно плащане"], [Truck, "Доставка до адрес"], [Award, "Сертификат за автентичност"]].map(([Icon, t]) => (
              <div key={t} className="flex items-center gap-2 text-xs text-charcoal/70"><Icon size={16} className="text-gold" />{t}</div>
            ))}
          </div>

          <Link to="/kontakti" className="flex items-center gap-2 text-sm text-navy mt-6 hover:text-gold" data-testid="ask-before-buy">
            <MessageCircle size={16} className="text-gold" /> Имате въпрос? Свържете се преди покупка.
          </Link>

          {product.description && (
            <div className="rich-content mt-8 pt-8 border-t border-stone text-charcoal/80"
                 dangerouslySetInnerHTML={{ __html: product.description.replace(/\\n/g, "<br/>") }} />
          )}
        </div>
      </div>

      {/* related / upsell */}
      {product.related?.length > 0 && (
        <div className="bg-white border-t border-stone py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="font-serif text-2xl md:text-3xl text-navy mb-8 text-center">Може да харесате също</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {product.related.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
